import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { 
  Wrench, ShieldCheck, Calculator as CalcIcon, MapPin, Send, MessageSquare, 
  X, Check, AlertTriangle, Settings, Phone, Mail, Clock, Trash2, 
  LogOut, Plus, ChevronRight, User, Award, Layers, Sparkles, CheckCircle2,
  Lock, RefreshCw, Star, Info, Menu
} from "lucide-react";
import { LOGIN, REGISTER, LOGOUT } from "./constants/testIds/auth";
import { SEPTIC } from "./constants/testIds/septic";
import { HelmetProvider } from "react-helmet-async";
import LocationServicePage from "./pages/LocationServicePage";
import ServiceAreasPage from "./pages/ServiceAreasPage";
import ServicePage from "./pages/ServicePage";
import { StickyMobileCTA, ExitIntentModal } from "./components/ConversionBoosters";
import LeadMagnet from "./components/LeadMagnet";
import GuidePage from "./pages/GuidePage";
import { LOCATIONS } from "./data/locations";
import { SERVICES } from "./data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_tank-replace-pro/artifacts/isbxf87e_Logo%20Kool%20Plumbing-01.png";

// Simple Toast Notification Component managed in App state
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgClass = type === "error" ? "bg-red-600" : "bg-emerald-600";

  return (
    <div className={`fixed bottom-5 right-5 z-50 flex items-center gap-2 px-5 py-3 rounded-lg text-white shadow-lg transition-all duration-300 animate-slide-up ${bgClass}`}>
      {type === "error" ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
      <span className="text-sm font-medium font-body">{message}</span>
      <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
        <X size={14} />
      </button>
    </div>
  );
};

// --- CHATBOT WIDGET ---
const ChatbotWidget = ({ addToast }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Septic Pro AI, your septic system expert. Ask me anything about septic tank replacement, permitting, soil tests, or general plumbing!", timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) }
  ]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const messagesEndRef = useRef(null);

  // Generate unique session ID per session load
  useEffect(() => {
    let sid = sessionStorage.getItem("septic_chat_session");
    if (!sid) {
      sid = "sess_" + Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem("septic_chat_session", sid);
    }
    setSessionId(sid);
    
    // Fetch chat history from DB if it exists
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${API}/chat/history/${sid}`);
        if (res.data.messages && res.data.messages.length > 0) {
          const formatted = res.data.messages.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: new Date(m.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
          }));
          setMessages(formatted);
        }
      } catch (err) {
        console.error("Error fetching chat history:", err);
      }
    };
    fetchHistory();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isStreaming]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;

    const userText = input.trim();
    setInput("");
    const nowStr = new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    
    // Add user message to state
    setMessages(prev => [...prev, { role: "user", content: userText, timestamp: nowStr }]);
    
    // Setup placeholder for assistant message
    setMessages(prev => [...prev, { role: "assistant", content: "", timestamp: nowStr }]);
    setIsStreaming(true);

    try {
      const response = await fetch(`${API}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ message: userText, session_id: sessionId })
      });

      if (!response.ok) {
        throw new Error("Chat service unavailable");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const dataStr = line.slice(6).trim();
            if (dataStr === "[DONE]") {
              setIsStreaming(false);
              break;
            }
            try {
              const parsed = JSON.parse(dataStr);
              if (parsed.text) {
                accumulatedText += parsed.text;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1].content = accumulatedText;
                  return updated;
                });
              } else if (parsed.error) {
                throw new Error(parsed.error);
              }
            } catch (err) {
              console.error("Error parsing chunk:", err);
            }
          }
        }
      }
    } catch (err) {
      console.error("Chat error:", err);
      addToast(err.message || "Failed to communicate with AI", "error");
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1].content = "Sorry, I ran into an error communicating with the server. Please try again.";
        return updated;
      });
      setIsStreaming(false);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          data-testid={SEPTIC.chatOpenButton}
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-50 bg-blue-900 hover:bg-blue-800 text-white p-4 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 border border-blue-700"
        >
          <MessageSquare size={24} />
          <span className="absolute -top-1 -right-1 bg-amber-600 w-3 h-3 rounded-full animate-ping"></span>
          <span className="absolute -top-1 -right-1 bg-amber-600 w-3 h-3 rounded-full"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-40 w-96 max-w-[calc(100vw-2rem)] h-[550px] max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col border border-gray-200 overflow-hidden font-body animate-scale-up">
          {/* Header */}
          <div className="bg-blue-900 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-600 flex items-center justify-center shadow">
                <Sparkles size={18} />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Septic Pro AI</h4>
                <p className="text-xs text-blue-200 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block animate-pulse"></span>
                  Online • Claude Sonnet 4.6
                </p>
              </div>
            </div>
            <button
              data-testid={SEPTIC.chatCloseButton}
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-white/10 rounded-full transition"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages Area */}
          <div
            data-testid={SEPTIC.chatHistoryContainer}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
          >
            {messages.map((m, idx) => (
              <div
                key={idx}
                data-testid={SEPTIC.chatMessageItem}
                className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm ${
                    m.role === "user"
                      ? "bg-blue-900 text-white rounded-tr-none"
                      : "bg-white text-gray-800 rounded-tl-none border border-gray-100 shadow-sm"
                  }`}
                >
                  {m.content ? (
                    <p className="whitespace-pre-line leading-relaxed">{m.content}</p>
                  ) : (
                    <div className="flex items-center gap-1.5 py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }}></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }}></span>
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-gray-400 mt-1 px-1">{m.timestamp}</span>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form onSubmit={handleSend} className="p-3 border-t border-gray-100 flex gap-2 bg-white">
            <input
              data-testid={SEPTIC.chatMessageInput}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question..."
              disabled={isStreaming}
              className="flex-1 h-11 px-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white text-sm"
            />
            <button
              data-testid={SEPTIC.chatSendButton}
              type="submit"
              disabled={isStreaming || !input.trim()}
              className="h-11 w-11 flex items-center justify-center rounded-xl bg-blue-900 text-white hover:bg-blue-800 disabled:bg-gray-100 disabled:text-gray-400 transition shadow-md hover:shadow-lg"
            >
              <Send size={18} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

// --- PUBLIC LANDING PAGE ---
const PublicLanding = ({ addToast, onLockEstimate }) => {
  // Calculator values state
  const [bedrooms, setBedrooms] = useState("3");
  const [usage, setUsage] = useState("moderate");
  const [soil, setSoil] = useState("silt");
  const [calcResult, setCalcResult] = useState(null);
  const [calcConfig, setCalcConfig] = useState(null);

  // Service Area Checker state
  const [zipInput, setZipInput] = useState("");
  const [zipResult, setZipResult] = useState(null);
  const [isCheckingZip, setIsCheckingZip] = useState(false);

  // Lead Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    zipCode: "",
    serviceType: "septic_replacement",
    message: ""
  });
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);

  // Fetch Pricing configs
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/calculator-settings`);
        setCalcConfig(res.data);
      } catch (err) {
        console.warn("Failed to fetch backend calculator settings, using robust client fallbacks", err);
        // Robust fallback configs
        setCalcConfig({
          base_replacement_cost: 7475.0,
          bedroom_multipliers: {"1-2": 1.0, "3": 1.25, "4": 1.5, "5+": 1.8},
          soil_multipliers: {"sandy": 1.0, "silt": 1.3, "dense": 1.6},
          usage_multipliers: {"low": 1.0, "moderate": 1.1, "heavy": 1.25}
        });
      }
    };
    fetchConfig();
  }, []);

  // Recalculate cost in real-time when inputs or configuration change
  useEffect(() => {
    if (!calcConfig) return;

    const base = calcConfig.base_replacement_cost;
    const bm = calcConfig.bedroom_multipliers[bedrooms] || 1.0;
    const sm = calcConfig.soil_multipliers[soil] || 1.0;
    const um = calcConfig.usage_multipliers[usage] || 1.0;

    const costValue = base * bm * sm * um;
    const minCost = Math.round(costValue * 0.9);
    const maxCost = Math.round(costValue * 1.1);

    let capacity = "1,000 Gallons";
    if (bedrooms === "4") capacity = "1,250 Gallons";
    else if (bedrooms === "5+") capacity = "1,500+ Gallons";

    setCalcResult({
      capacity,
      costRange: `$${minCost.toLocaleString()} - $${maxCost.toLocaleString()}`,
      minCost,
      maxCost
    });
  }, [bedrooms, usage, soil, calcConfig]);

  // Check served ZIP areas
  const handleZipCheck = async (e) => {
    e.preventDefault();
    if (!zipInput.trim()) return;

    setIsCheckingZip(true);
    setZipResult(null);

    try {
      const res = await axios.get(`${API}/service-areas/check/${zipInput.trim()}`);
      setZipResult(res.data);
    } catch (err) {
      console.error("Zip check error:", err);
      addToast("Failed to verify service area", "error");
    } finally {
      setIsCheckingZip(false);
    }
  };

  // Lock-in Estimate CTA
  const handleLockEstimate = () => {
    if (!calcResult) return;
    onLockEstimate({
      bedroom_count: bedrooms,
      usage_level: usage,
      soil_type: soil,
      estimated_cost: Math.round((calcResult.minCost + calcResult.maxCost) / 2)
    });
    
    // Scroll to contact form
    const formSection = document.getElementById("quote-section");
    if (formSection) {
      formSection.scrollIntoView({ behavior: "smooth" });
    }

    // Prefill form
    setFormData(prev => ({
      ...prev,
      serviceType: "septic_replacement",
      message: `Locked-in Septic replacement cost estimate: ${calcResult.costRange} for a ${calcResult.capacity} tank setup (${bedrooms} Bedrooms, ${usage} usage, ${soil} soil).`
    }));

    addToast("Estimate locked-in! Complete the form below to lock your price.", "success");
  };

  // Submit Lead request
  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingLead(true);
    setLeadSuccess(false);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        zip_code: formData.zipCode,
        service_type: formData.serviceType,
        message: formData.message,
        // Include calculator metadata if locked
        bedroom_count: bedrooms,
        usage_level: usage,
        soil_type: soil,
        estimated_cost: calcResult ? Math.round((calcResult.minCost + calcResult.maxCost) / 2) : null
      };

      await axios.post(`${API}/leads`, payload);
      setLeadSuccess(true);
      addToast("Your quote request was submitted successfully! Our expert plumber will contact you shortly.", "success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        zipCode: "",
        serviceType: "septic_replacement",
        message: ""
      });
    } catch (err) {
      console.error("Lead submission error:", err);
      addToast("Failed to submit request. Please verify inputs and try again.", "error");
    } finally {
      setIsSubmittingLead(false);
    }
  };

  return (
    <div className="flex-1 font-body">
      <ExitIntentModal />
      {/* HERO SECTION */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1759355787118-7b48305965d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjBzdWJ1cmJhbiUyMGhvbWUlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODIxODk0ODB8MA&ixlib=rb-4.1.0&q=85')" }}>
        <div className="absolute inset-0 bg-slate-900/70"></div>
        <div className="relative max-w-6xl mx-auto px-6 text-center text-white z-10 space-y-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-600/30 border border-amber-600/50 text-amber-300 text-sm font-semibold animate-fade-in">
            <Award size={16} /> #1 Rated Septic Experts & Full-Service Plumbing
          </div>
          <h1 className="text-4xl sm:text-6xl font-heading leading-tight tracking-tight max-w-4xl mx-auto">
            Professional Septic Tank Replacement & Quality Plumbing
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Ground-level engineering with an emphasis on flawless, long-lasting septic replacements. High-quality soil percolation testing, rapid permitting, and expert installations.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <a
              href="#calculator"
              className="px-8 py-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold tracking-wide shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
            >
              Get Septic Cost Estimate
            </a>
            <a
              href="#services"
              className="px-8 py-4 rounded-xl bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold tracking-wide backdrop-blur transition-all duration-200"
            >
              Explore Our Services
            </a>
          </div>
        </div>
      </section>

      {/* REASSURANCE BAR */}
      <section className="bg-blue-950 py-6 border-b border-blue-900">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white font-body">
          <div className="flex flex-col items-center justify-center space-y-1">
            <span className="text-amber-500 font-heading text-2xl font-bold">15+ Years</span>
            <span className="text-xs text-gray-400">Serving Local Homeowners</span>
          </div>
          <div className="flex flex-col items-center justify-center space-y-1">
            <span className="text-amber-500 font-heading text-2xl font-bold">Free Quote</span>
            <span className="text-xs text-gray-400">On-Site Septic Inspections</span>
          </div>
          <div className="flex flex-col items-center justify-center space-y-1">
            <span className="text-amber-500 font-heading text-2xl font-bold">Licensed</span>
            <span className="text-xs text-gray-400">EPA & State Environmental Certs</span>
          </div>
          <div className="flex flex-col items-center justify-center space-y-1">
            <span className="text-amber-500 font-heading text-2xl font-bold">24/7 Care</span>
            <span className="text-xs text-gray-400">Emergency Septic Overflows</span>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 space-y-16">
          <div className="text-center space-y-4 max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-heading text-blue-900 uppercase tracking-wide">
              Expert Solutions We Deliver
            </h2>
            <p className="text-gray-500 font-body">
              Whether you need complex septic excavation or a simple household drain fix, our experienced team provides elite workmanship.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Highly Highlighted Septic Replacement Service */}
            <div className="space-y-6">
              <div className="inline-block px-3 py-1 bg-amber-100 border border-amber-200 rounded text-amber-800 text-xs font-bold uppercase tracking-widest font-heading">
                Our Signature Specialty
              </div>
              <h3 className="text-3xl font-heading text-blue-950">
                Septic Tank Excavation & Full Replacements
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Aging or failing septic systems cause pooling water, backups, and environmental fines. Our dedicated engineers coordinate the entire replacement roadmap:
              </p>
              <ul className="space-y-3 font-body text-sm text-gray-600">
                <li className="flex items-start gap-3">
                  <span className="text-emerald-500 mt-1"><CheckCircle2 size={16} /></span>
                  <span><strong>Comprehensive Soil Perc Testing</strong> to map accurate absorption capability.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-500 mt-1"><CheckCircle2 size={16} /></span>
                  <span><strong>Council Permitting & CAD Designing</strong> handled 100% in-house.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-500 mt-1"><CheckCircle2 size={16} /></span>
                  <span><strong>Premium Precast Concrete / Poly Tanks</strong> for optimal lifespan.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-emerald-500 mt-1"><CheckCircle2 size={16} /></span>
                  <span><strong>Drain Field Reclamation</strong> with advanced gravel-less chamber technology.</span>
                </li>
              </ul>
              <div className="pt-2 flex flex-wrap gap-4 items-center">
                <Link to="/services/septic-tank-replacement" data-testid="home-service-septic-link" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-900 hover:bg-blue-800 text-white font-bold font-heading transition shadow">
                  View Septic Replacement <ChevronRight size={18} />
                </Link>
                <a href="#calculator" className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 font-bold font-heading">
                  Run Cost Estimator <ChevronRight size={18} />
                </a>
              </div>
            </div>

            {/* Service image showing real场景 */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group border border-gray-100">
              <img
                src="https://images.pexels.com/photos/32588548/pexels-photo-32588548.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Expert technician"
                className="w-full h-[400px] object-cover object-center group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white space-y-1">
                <p className="font-heading font-semibold text-lg">Elite Craftsmanship</p>
                <p className="text-xs text-gray-200">Our engineers are fully certified and insured.</p>
              </div>
            </div>
          </div>

          {/* Grid of Other General Plumbing Services */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 pt-8">
            <Link to="/services/drain-field-repair" data-testid="home-service-drain-field-repair" className="group p-8 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-amber-300 transition space-y-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-900 flex items-center justify-center">
                <Wrench size={24} />
              </div>
              <h4 className="text-xl font-heading text-blue-950 flex items-center justify-between">Drain Field Repair <ChevronRight size={18} className="text-gray-300 group-hover:text-amber-600 group-hover:translate-x-0.5 transition" /></h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Slow drainage or toilet bubbling? We perform biological rejuvenation and mechanical aeration to clear clay clog build-ups.
              </p>
              <span className="text-xs font-bold text-amber-700 font-heading">Learn more →</span>
            </Link>

            <Link to="/services/septic-pumping" data-testid="home-service-septic-pumping" className="group p-8 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-amber-300 transition space-y-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-900 flex items-center justify-center">
                <ShieldCheck size={24} />
              </div>
              <h4 className="text-xl font-heading text-blue-950 flex items-center justify-between">Septic Pumping &amp; Care <ChevronRight size={18} className="text-gray-300 group-hover:text-amber-600 group-hover:translate-x-0.5 transition" /></h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Routine cleanouts prevent solids from entering your absorption field. Recommended every 3 to 5 years for healthy systems.
              </p>
              <span className="text-xs font-bold text-amber-700 font-heading">Learn more →</span>
            </Link>

            <Link to="/services/emergency-plumbing" data-testid="home-service-emergency-plumbing" className="group p-8 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-amber-300 transition space-y-4 col-span-1 sm:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-900 flex items-center justify-center">
                <Layers size={24} />
              </div>
              <h4 className="text-xl font-heading text-blue-950 flex items-center justify-between">24/7 Emergency Plumbing <ChevronRight size={18} className="text-gray-300 group-hover:text-amber-600 group-hover:translate-x-0.5 transition" /></h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                Burst lines, tankless water heater retrofits, sewer camera diagnostics, and round-the-clock emergency response.
              </p>
              <span className="text-xs font-bold text-amber-700 font-heading">Learn more →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* FINANCING & TRUST SECTION */}
      <section className="py-20 bg-blue-950 text-white relative overflow-hidden" data-testid="financing-trust-section">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <span className="text-amber-400 text-xs font-bold uppercase tracking-widest font-heading">Affordable & Worry-Free</span>
            <h2 className="text-3xl sm:text-4xl font-heading leading-tight">Financing Available — Don't Let Cost Delay a Failing Septic System</h2>
            <p className="text-gray-300 leading-relaxed font-body">
              A septic emergency shouldn't drain your savings. We offer flexible financing options so you can get your system replaced now and pay over time — with transparent, upfront pricing and absolutely no hidden fees.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <a href="#quote-section" data-testid="financing-quote-btn" className="px-8 py-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold tracking-wide shadow-lg transition-all transform hover:-translate-y-0.5 text-center">Ask About Financing</a>
              <a href="tel:+17866224963" className="px-8 py-4 rounded-xl bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold backdrop-blur transition-all flex items-center justify-center gap-2"><Phone size={18} /> (786) 622-4963</a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              [ShieldCheck, "Licensed & Insured", "FL Certified #CFC1432999"],
              [Award, "Workmanship Warranty", "We stand behind every install"],
              [Layers, "Flexible Financing", "Pay over time, no big upfront hit"],
              [Clock, "Upfront Pricing", "No surprises, no hidden fees"],
            ].map(([Icon, t, d]) => (
              <div key={t} className="p-6 bg-white/5 border border-white/10 rounded-2xl space-y-2 hover:bg-white/10 transition">
                <span className="w-11 h-11 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center"><Icon size={22} /></span>
                <h4 className="font-heading text-base text-white">{t}</h4>
                <p className="text-xs text-gray-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* CALCULATOR & SERVICE ZIP CHECKER SECTION */}
      <section id="calculator" className="py-24 bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6 space-y-16">
          <div className="text-center space-y-4 max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-heading text-blue-900 uppercase tracking-wide">
              Septic System Estimator & Area Check
            </h2>
            <p className="text-gray-500 font-body">
              Input your household needs to calculate a highly accurate septic replacement cost range, and verify dispatch service availability instantly.
            </p>
          </div>

          {/* Bento-style grid for Estimator & Service Checker */}
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            
            {/* Left: Input parameters panel (col-span-5) */}
            <div className="lg:col-span-5 bg-white border border-gray-200 rounded-2xl p-8 shadow-sm space-y-6">
              <h3 className="text-xl font-heading text-blue-950 flex items-center gap-2">
                <CalcIcon size={20} className="text-amber-600" /> Enter Property Specs
              </h3>
              
              {/* Bedroom count Selector */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 block">Household Size (Bedrooms)</label>
                <select
                  data-testid={SEPTIC.bedroomsSelect}
                  value={bedrooms}
                  onChange={(e) => setBedrooms(e.target.value)}
                  className="w-full h-12 px-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:bg-white text-sm focus:outline-none transition font-body"
                >
                  <option value="1-2">1-2 Bedrooms (Compact/Cottage)</option>
                  <option value="3">3 Bedrooms (Standard Family Home)</option>
                  <option value="4">4 Bedrooms (Large Residential)</option>
                  <option value="5+">5+ Bedrooms (Premium Estate)</option>
                </select>
                <p className="text-xs text-gray-400">EPA regulates septic tank sizes strictly according to maximum bedroom occupancy.</p>
              </div>

              {/* Water usage level */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 block">Estimated Household Usage</label>
                <div className="grid grid-cols-3 gap-2">
                  {["low", "moderate", "heavy"].map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setUsage(u)}
                      className={`h-11 rounded-lg border text-xs font-semibold capitalize transition-all ${
                        usage === u
                          ? "bg-blue-900 border-blue-900 text-white shadow-sm"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-600"
                      }`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>

              {/* Soil excavation difficulty */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 block">Soil Profile (Excavation Soil)</label>
                <select
                  data-testid={SEPTIC.soilSelect}
                  value={soil}
                  onChange={(e) => setSoil(e.target.value)}
                  className="w-full h-12 px-4 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:bg-white text-sm focus:outline-none transition font-body"
                >
                  <option value="sandy">Sandy / Gravel Soil (Fast Perc, Low Complexity)</option>
                  <option value="silt">Silt / Silt-Clay Mixture (Standard Setup)</option>
                  <option value="dense">Dense Clay / Heavy Rock Bed (Requires Jackhammer Excavation)</option>
                </select>
              </div>
            </div>

            {/* Right: Real-time estimate output & Service checker (col-span-7) */}
            <div className="lg:col-span-7 space-y-8">
              
              {/* Calculator Output Display */}
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm space-y-6 relative overflow-hidden">
                {/* Visual texture overlay */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -z-0 opacity-40"></div>
                
                <div className="relative z-10 space-y-4">
                  <span className="px-3 py-1 bg-blue-50 text-blue-900 border border-blue-100 rounded text-xs font-bold uppercase tracking-wider font-heading">
                    Real-time Estimate
                  </span>
                  <div className="grid sm:grid-cols-2 gap-6 pt-2">
                    <div className="space-y-1">
                      <span className="text-xs text-gray-400 font-semibold block uppercase">Recommended Tank Volume</span>
                      <span className="text-xl font-heading text-blue-950 block">{calcResult ? calcResult.capacity : "Loading..."}</span>
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-gray-400 font-semibold block uppercase">Estimated Installation Range</span>
                      <span data-testid={SEPTIC.estimationResult} className="text-2xl font-heading text-amber-700 font-bold block">{calcResult ? calcResult.costRange : "Loading..."}</span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 leading-relaxed font-body">
                    *Range accounts for excavation machinery, high-grade piping, state-approved precast tank, final environmental signoff, and post-install grass reseeding. Does not include municipal filing fees.
                  </p>

                  <div className="pt-2">
                    <button
                      data-testid={SEPTIC.lockPriceButton}
                      onClick={handleLockEstimate}
                      className="w-full h-14 bg-amber-600 hover:bg-amber-700 text-white font-bold tracking-wide rounded-xl shadow-md hover:shadow-lg transition-transform transform active:scale-95 flex items-center justify-center gap-2"
                    >
                      <Lock size={18} /> Lock in this Septic Estimate
                    </button>
                  </div>
                </div>
              </div>

              {/* Service Checker card */}
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm space-y-6">
                <h3 className="text-xl font-heading text-blue-950 flex items-center gap-2">
                  <MapPin size={20} className="text-blue-900" /> Verify Your Service Location
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  We deploy heavy excavation rigs within strict municipal limits. Input your Zip code to verify immediate dispatch scheduling.
                </p>

                <form onSubmit={handleZipCheck} className="flex gap-2">
                  <input
                    data-testid={SEPTIC.zipInput}
                    type="text"
                    required
                    maxLength={10}
                    placeholder="Enter Zip Code (e.g. 90210)"
                    value={zipInput}
                    onChange={(e) => setZipInput(e.target.value)}
                    className="flex-1 h-12 px-4 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white text-sm"
                  />
                  <button
                    data-testid={SEPTIC.checkZipButton}
                    type="submit"
                    disabled={isCheckingZip || !zipInput.trim()}
                    className="h-12 px-6 rounded-xl bg-blue-900 hover:bg-blue-800 text-white text-sm font-semibold tracking-wide shadow-md transition disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    {isCheckingZip ? <RefreshCw className="animate-spin" size={16} /> : "Check Area"}
                  </button>
                </form>

                {zipResult && (
                  <div
                    data-testid={SEPTIC.checkerResult}
                    className={`p-4 rounded-xl border text-sm flex gap-3 items-start animate-fade-in ${
                      zipResult.is_served
                        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                        : "bg-red-50 border-red-100 text-red-800"
                    }`}
                  >
                    <div className="mt-0.5">
                      {zipResult.is_served ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
                    </div>
                    <div>
                      <p className="font-semibold">{zipResult.is_served ? "Dispatch Available!" : "Outside Service Bounds"}</p>
                      <p className="text-xs mt-0.5 leading-relaxed opacity-90">{zipResult.message}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD MAGNET — Free Guide Download */}
      <LeadMagnet addToast={addToast} />


      {/* REQUEST QUOTE LEAD FORM */}
      <section id="quote-section" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl sm:text-4xl font-heading text-blue-900 uppercase tracking-wide">
              Request Your Free Quote
            </h2>
            <p className="text-gray-500 font-body max-w-lg mx-auto">
              Ready to replace or repair? Leave your project details below. Our certified septic technician will call you within 2 business hours.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-100 rounded-3xl p-8 sm:p-12 shadow-md">
            <form onSubmit={handleLeadSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Full name */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Full Name *</label>
                  <input
                    data-testid={SEPTIC.leadNameInput}
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="John Doe"
                    className="w-full h-12 px-4 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm"
                  />
                </div>

                {/* Email address */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Email Address *</label>
                  <input
                    data-testid={SEPTIC.leadEmailInput}
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="john@example.com"
                    className="w-full h-12 px-4 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {/* Phone number */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Phone Number *</label>
                  <input
                    data-testid={SEPTIC.leadPhoneInput}
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    className="w-full h-12 px-4 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm"
                  />
                </div>

                {/* Zip code */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Property Zip Code *</label>
                  <input
                    data-testid={SEPTIC.leadZipInput}
                    type="text"
                    required
                    value={formData.zipCode}
                    onChange={(e) => setFormData({...formData, zipCode: e.target.value})}
                    placeholder="90210"
                    className="w-full h-12 px-4 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm"
                  />
                </div>
              </div>

              {/* Service Requested */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Service Requested *</label>
                <select
                  data-testid={SEPTIC.leadServiceSelect}
                  value={formData.serviceType}
                  onChange={(e) => setFormData({...formData, serviceType: e.target.value})}
                  className="w-full h-12 px-4 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm"
                >
                  <option value="septic_replacement">Septic Tank Full Replacement (High Priority)</option>
                  <option value="septic_repair">Drain Field or Tank System Repairs</option>
                  <option value="septic_pumping">Routine Maintenance Pump Out</option>
                  <option value="general_plumbing">General Residential Plumbing Leak/Line Fix</option>
                </select>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Describe Your Problem / Project Info</label>
                <textarea
                  data-testid={SEPTIC.leadMessageInput}
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Tell us about the issue (e.g. wet soil in garden, backups inside home, kitchen faucet issues...)"
                  className="w-full p-4 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm"
                ></textarea>
              </div>

              {/* Submit Lead Button */}
              <div>
                <button
                  data-testid={SEPTIC.leadSubmitButton}
                  type="submit"
                  disabled={isSubmittingLead}
                  className="w-full h-14 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-xl shadow-lg transition disabled:bg-gray-200 disabled:text-gray-400 flex items-center justify-center gap-2"
                >
                  {isSubmittingLead ? <RefreshCw className="animate-spin" size={18} /> : "Submit Free Quote Request"}
                </button>
              </div>
            </form>

            {leadSuccess && (
              <div
                data-testid={SEPTIC.leadSuccessAlert}
                className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex gap-2 items-center animate-fade-in"
              >
                <CheckCircle2 size={18} />
                <span>Success! Quote details logged. Our specialist will call you shortly.</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* REVIEWS SECTION */}
      <section className="py-24 bg-gray-50 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl font-heading text-blue-950 uppercase tracking-wide">Client Testimonials</h2>
            <p className="text-gray-500 text-sm font-body">Read verified experiences from homeowners who trusted Kool Plumbing with their properties.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl border border-gray-200 space-y-4 shadow-sm">
              <div className="flex gap-1 text-amber-500">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed font-body">
                “Our septic system completely backed up on a Sunday. Kool Plumbing was incredibly professional—they scoped the tank, handled the county permitting, and excavated within 48 hours. Phenomenal team.”
              </p>
              <div className="pt-2">
                <p className="font-heading font-semibold text-sm text-blue-950">Gareth M.</p>
                <p className="text-[10px] text-gray-400">Miami, FL • Verified Owner</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-200 space-y-4 shadow-sm">
              <div className="flex gap-1 text-amber-500">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed font-body">
                “We locked our septic estimate on the website and everything was perfectly clear. The price range they gave was exact. They finished the job on time and re-seeded the lawn beautifully.”
              </p>
              <div className="pt-2">
                <p className="font-heading font-semibold text-sm text-blue-950">Sarah L.</p>
                <p className="text-[10px] text-gray-400">Fort Lauderdale, FL • Verified Owner</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-200 space-y-4 shadow-sm">
              <div className="flex gap-1 text-amber-500">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed font-body">
                “Kool Plumbing replaced my mother’s septic drain field and did general line plumbing inside her house. They were honest, fast, and took time to explain the biological treatments. Best plumbing experience.”
              </p>
              <div className="pt-2">
                <p className="font-heading font-semibold text-sm text-blue-950">Marcus K.</p>
                <p className="text-[10px] text-gray-400">Pembroke Pines, FL • Verified Owner</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-blue-950 border-t border-blue-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 md:grid-cols-4 gap-8">
          <div className="space-y-4">
            <img src={LOGO_URL} alt="Kool Plumbing" className="h-14 w-auto" />
            <p className="text-xs text-gray-300 leading-relaxed">
              Leading residential engineering. Focused on superior precast tank replacements, percolation site mapping, and trusted master plumbing.
            </p>
          </div>
          <div className="space-y-4 font-body text-xs text-gray-300">
            <h5 className="font-heading font-semibold text-sm text-white">Service Areas</h5>
            <ul className="space-y-2">
              {["fort-lauderdale", "pembroke-pines", "hollywood", "coral-gables", "miami-beach", "doral"].map((s) => {
                const l = LOCATIONS.find((x) => x.slug === s);
                return l ? (
                  <li key={s}><Link to={`/service-areas/${s}`} className="hover:text-amber-500" data-testid={`footer-link-${s}`}>{l.city}</Link></li>
                ) : null;
              })}
              <li><Link to="/service-areas" className="text-amber-500 hover:underline font-semibold">View all areas →</Link></li>
            </ul>
          </div>
          <div className="space-y-4 font-body text-xs text-gray-300">
            <h5 className="font-heading font-semibold text-sm text-white">Services</h5>
            <ul className="space-y-2">
              {SERVICES.map((s) => (
                <li key={s.slug}><Link to={`/services/${s.slug}`} className="hover:text-amber-500" data-testid={`footer-service-${s.slug}`}>{s.short}</Link></li>
              ))}
              <li><a href="/#calculator" className="hover:text-amber-500">Cost Estimator</a></li>
              <li><Link to="/admin" className="hover:text-amber-500">Admin Control Panel</Link></li>
            </ul>
          </div>
          <div className="space-y-4 font-body text-xs text-gray-300">
            <h5 className="font-heading font-semibold text-sm text-white">Contact</h5>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-center gap-2"><Phone size={14} className="text-amber-500" /> <a href="tel:+17866224963" className="hover:text-amber-500">(786) 622-4963</a></li>
              <li className="flex items-center gap-2"><Mail size={14} className="text-amber-500" /> <a href="mailto:info@koolplumbing.com" className="hover:text-amber-500">info@koolplumbing.com</a></li>
              <li className="flex items-start gap-2"><MapPin size={14} className="text-amber-500 mt-0.5" /> <span>4346 SW 74 Ave, Miami, FL<br/>Serving Miami-Dade &amp; Broward</span></li>
              <li className="flex items-center gap-2"><Clock size={14} className="text-amber-500" /> Mon - Sun: 24 Hour Emergency</li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 border-t border-blue-900 mt-8 pt-6 text-center text-xs text-gray-400">
          <p>© 2026 Kool Plumbing. All rights reserved. State Certified Plumbing Contractor — License #CFC1432999.</p>
        </div>
      </footer>
    </div>
  );
};

// --- ADMIN DASHBOARD PAGE ---
const AdminDashboard = ({ addToast }) => {
  const [token, setToken] = useState(localStorage.getItem("septic_admin_token") || "");
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Nav sub-section inside dashboard
  const [activeTab, setActiveTab] = useState("leads"); // "leads" | "chats" | "settings"

  // Data states
  const [leads, setLeads] = useState([]);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [settings, setSettings] = useState(null);
  const [zipsString, setZipsString] = useState("");
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const navigate = useNavigate();

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: emailInput,
        password: passwordInput
      });
      localStorage.setItem("septic_admin_token", res.data.token);
      localStorage.setItem("septic_admin_email", res.data.email);
      setToken(res.data.token);
      addToast("Successfully logged into Admin Panel", "success");
    } catch (err) {
      console.error("Login error:", err);
      addToast(err.response?.data?.detail || "Invalid admin credentials", "error");
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem("septic_admin_token");
    localStorage.removeItem("septic_admin_email");
    setToken("");
    setLeads([]);
    setChats([]);
    setSettings(null);
    addToast("Logged out from Admin Panel", "success");
  };

  // Auth interceptor helper to make requests with Authorization token
  const authGet = async (url) => {
    return await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
  };

  const authPut = async (url, payload) => {
    return await axios.put(url, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });
  };

  const authDelete = async (url) => {
    return await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
  };

  // Fetch admin data
  const fetchAllData = async () => {
    if (!token) return;
    setIsLoadingData(true);
    try {
      // 1. Fetch leads
      const leadsRes = await authGet(`${API}/leads`);
      setLeads(leadsRes.data);

      // 2. Fetch Chat sessions
      const chatsRes = await authGet(`${API}/chats`);
      setChats(chatsRes.data);

      // 3. Fetch Pricing Settings
      const settingsRes = await axios.get(`${API}/calculator-settings`);
      setSettings(settingsRes.data);

      // 4. Fetch Zip codes
      const zipsRes = await axios.get(`${API}/service-areas`);
      setZipsString(zipsRes.data.zip_codes.join(", "));

    } catch (err) {
      console.error("Admin fetch error:", err);
      if (err.response?.status === 401) {
        addToast("Session expired. Please log in again.", "error");
        handleLogout();
      } else {
        addToast("Failed to fetch admin data dashboard", "error");
      }
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchAllData();
    }
  }, [token]);

  // Lead status change handler
  const handleStatusChange = async (leadId, newStatus) => {
    try {
      const res = await authPut(`${API}/leads/${leadId}/status`, { status: newStatus });
      setLeads(prev => prev.map(l => l._id === leadId ? { ...l, status: res.data.status } : l));
      addToast(`Lead status updated to ${newStatus}`, "success");
    } catch (err) {
      console.error("Status update error:", err);
      addToast("Failed to change lead status", "error");
    }
  };

  // Lead delete handler
  const handleLeadDelete = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead? This action is permanent.")) return;
    try {
      await authDelete(`${API}/leads/${leadId}`);
      setLeads(prev => prev.filter(l => l._id !== leadId));
      addToast("Lead deleted successfully", "success");
    } catch (err) {
      console.error("Lead delete error:", err);
      addToast("Failed to delete lead", "error");
    }
  };

  // Save Settings handler
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setIsSavingSettings(true);
    try {
      // Save Pricing configs
      const updatedPricing = await authPut(`${API}/calculator-settings`, {
        base_replacement_cost: settings.base_replacement_cost,
        bedroom_multipliers: settings.bedroom_multipliers,
        soil_multipliers: settings.soil_multipliers,
        usage_multipliers: settings.usage_multipliers
      });
      setSettings(updatedPricing.data);

      // Save served Zip codes
      const zipsArray = zipsString
        .split(",")
        .map(z => z.trim())
        .filter(z => z !== "");
        
      await authPut(`${API}/service-areas`, { zip_codes: zipsArray });

      addToast("Pricing parameters and service areas saved successfully!", "success");
    } catch (err) {
      console.error("Save settings error:", err);
      addToast("Failed to save configuration settings", "error");
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Helper for status classes
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "scheduled": return "bg-blue-100 text-blue-800 border-blue-200";
      case "contacted": return "bg-amber-100 text-amber-800 border-amber-200";
      case "cancelled": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // If NOT logged in, show Login Form
  if (!token) {
    return (
      <div className="flex-1 min-h-[80vh] flex items-center justify-center p-6 bg-gray-50 font-body">
        <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8 shadow-md space-y-6">
          <div className="text-center space-y-2">
            <div className="w-12 h-12 bg-blue-100 text-blue-900 rounded-full flex items-center justify-center mx-auto shadow-sm">
              <Lock size={22} />
            </div>
            <h2 className="text-2xl font-heading text-blue-950 font-bold">Admin Portal Lock</h2>
            <p className="text-xs text-gray-400">Authenticated staff access only. Enter credentials to manage leads.</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-600">Admin Email</label>
              <input
                data-testid={LOGIN.emailInput}
                type="email"
                required
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="info@koolplumbing.com"
                className="w-full h-11 px-4 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-600">Password</label>
              <input
                data-testid={LOGIN.passwordInput}
                type="password"
                required
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="••••••••"
                className="w-full h-11 px-4 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm"
              />
            </div>

            <button
              data-testid={LOGIN.submitButton}
              type="submit"
              disabled={isLoggingIn}
              className="w-full h-11 bg-blue-900 hover:bg-blue-800 text-white font-bold tracking-wide rounded-xl shadow transition disabled:bg-gray-200"
            >
              {isLoggingIn ? <RefreshCw className="animate-spin" size={16} /> : "Log In"}
            </button>
          </form>
          <div className="text-center pt-2">
            <Link to="/" className="text-xs text-blue-700 hover:underline">← Return to Landing Page</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-100 min-h-[90vh] font-body flex flex-col">
      {/* Dashboard Sub-Header */}
      <div className="bg-white border-b border-gray-200 py-4 px-6 shadow-sm">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Kool Plumbing" className="h-11 w-auto" />
            <div>
              <h2 className="text-lg font-heading text-blue-950 font-bold">Admin Console Panel</h2>
              <p className="text-xs text-gray-400">Manage client pipeline, AI chats, and calculator parameters</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium">
              Active: {localStorage.getItem("septic_admin_email")}
            </span>
            <button
              data-testid={LOGOUT.button}
              onClick={handleLogout}
              className="p-2 hover:bg-red-50 text-red-600 rounded-xl transition flex items-center gap-1.5 text-xs font-semibold border border-transparent hover:border-red-100"
            >
              <LogOut size={16} /> Log Out
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Metrics Bar */}
      <div className="bg-gray-50 py-6 border-b border-gray-200 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-1">
            <span className="text-xs text-gray-400 uppercase font-semibold">Total Inquiries</span>
            <span className="text-2xl font-heading text-blue-950 font-bold block">{leads.length}</span>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-1">
            <span className="text-xs text-gray-400 uppercase font-semibold">Pending Review</span>
            <span className="text-2xl font-heading text-amber-600 font-bold block">
              {leads.filter(l => l.status?.toLowerCase() === "pending").length}
            </span>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-1">
            <span className="text-xs text-gray-400 uppercase font-semibold">AI Assistant Chats</span>
            <span className="text-2xl font-heading text-blue-950 font-bold block">{chats.length}</span>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-1">
            <span className="text-xs text-gray-400 uppercase font-semibold">Base Cost Rate</span>
            <span className="text-2xl font-heading text-emerald-700 font-bold block">
              ${settings ? settings.base_replacement_cost.toLocaleString() : "..."}
            </span>
          </div>
        </div>
      </div>

      {/* Dashboard Layout Body */}
      <div className="max-w-6xl w-full mx-auto p-6 flex-1 flex flex-col gap-6">
        {/* Navigation Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            data-testid={SEPTIC.adminNavLeads}
            onClick={() => setActiveTab("leads")}
            className={`px-5 py-3 text-sm font-semibold transition ${
              activeTab === "leads"
                ? "text-blue-900 border-b-2 border-blue-900"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            Leads Pipeline ({leads.length})
          </button>
          <button
            data-testid={SEPTIC.adminNavChats}
            onClick={() => {
              setActiveTab("chats");
              setSelectedChat(null);
            }}
            className={`px-5 py-3 text-sm font-semibold transition ${
              activeTab === "chats"
                ? "text-blue-900 border-b-2 border-blue-900"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            AI Conversations ({chats.length})
          </button>
          <button
            data-testid={SEPTIC.adminNavSettings}
            onClick={() => setActiveTab("settings")}
            className={`px-5 py-3 text-sm font-semibold transition ${
              activeTab === "settings"
                ? "text-blue-900 border-b-2 border-blue-900"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            Pricing & Locations Settings
          </button>
        </div>

        {/* LOADING INDICATOR */}
        {isLoadingData && (
          <div className="py-20 text-center space-y-4 bg-white rounded-2xl border border-gray-200 shadow-sm flex-1 flex flex-col items-center justify-center">
            <RefreshCw className="animate-spin text-blue-900" size={32} />
            <p className="text-sm text-gray-500 font-medium">Synchronizing database models...</p>
          </div>
        )}

        {/* Tab 1: Leads list */}
        {!isLoadingData && activeTab === "leads" && (
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex-1">
            {leads.length === 0 ? (
              <div className="py-20 text-center space-y-2 text-gray-400">
                <CalcIcon size={40} className="mx-auto text-gray-300" />
                <p className="font-semibold text-gray-500">No client submissions yet</p>
                <p className="text-xs">Once users fill out the landing page quotes, they will populate here in real-time.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table data-testid={SEPTIC.leadsTable} className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-400 uppercase">
                      <th className="p-4">Contact</th>
                      <th className="p-4">Requested Service</th>
                      <th className="p-4">Zip Code</th>
                      <th className="p-4">Locked Spec / Cost</th>
                      <th className="p-4">Submission Date</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 font-body">
                    {leads.map((l) => (
                      <tr key={l._id} className="hover:bg-gray-50/50">
                        {/* Name & Phone */}
                        <td className="p-4 space-y-1">
                          <p className="font-semibold text-blue-950">{l.name}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Mail size={12} /> {l.email}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Phone size={12} /> {l.phone}
                          </p>
                        </td>

                        {/* Service requested */}
                        <td className="p-4 capitalize">
                          <span className="font-medium text-gray-800">
                            {l.service_type?.replace("_", " ")}
                          </span>
                          {l.message && (
                            <p className="text-xs text-gray-400 mt-1 max-w-xs truncate" title={l.message}>
                              {l.message}
                            </p>
                          )}
                        </td>

                        {/* Location Zip */}
                        <td className="p-4 font-mono font-medium text-gray-600">{l.zip_code}</td>

                        {/* Specs locked and cost */}
                        <td className="p-4">
                          {l.bedroom_count ? (
                            <div className="space-y-1 text-xs">
                              <span className="block text-gray-700">Bedrooms: {l.bedroom_count} | Soil: {l.soil_type}</span>
                              <span className="font-semibold text-emerald-700">Cost: ${l.estimated_cost?.toLocaleString()}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>

                        {/* Created Date */}
                        <td className="p-4 text-xs text-gray-500">
                          {new Date(l.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                        </td>

                        {/* Status update dropdown */}
                        <td className="p-4">
                          <select
                            data-testid={SEPTIC.leadStatusSelect}
                            value={l.status || "Pending"}
                            onChange={(e) => handleStatusChange(l._id, e.target.value)}
                            className={`px-3 py-1.5 rounded-full border text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-blue-600 cursor-pointer ${getStatusBadgeClass(l.status)}`}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Contacted">Contacted</option>
                            <option value="Scheduled">Scheduled</option>
                            <option value="Cancelled">Cancelled</option>
                          </select>
                        </td>

                        {/* Delete row */}
                        <td className="p-4 text-right">
                          <button
                            data-testid={SEPTIC.leadDeleteButton}
                            onClick={() => handleLeadDelete(l._id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Delete Lead"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Chat log listing and details */}
        {!isLoadingData && activeTab === "chats" && (
          <div className="grid lg:grid-cols-12 gap-6 items-stretch flex-1">
            
            {/* Active chat session items (col-span-4) */}
            <div className="lg:col-span-5 bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-4 flex flex-col max-h-[600px] overflow-y-auto">
              <h3 className="font-heading font-semibold text-sm text-blue-950 px-2 uppercase tracking-wider">
                Select Conversation Thread
              </h3>
              {chats.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-10">No active AI chats logged in database.</p>
              ) : (
                <div className="space-y-2 flex-1">
                  {chats.map((c) => (
                    <button
                      key={c.session_id}
                      data-testid={SEPTIC.chatSessionItem}
                      onClick={() => setSelectedChat(c)}
                      className={`w-full text-left p-4 rounded-xl border text-xs transition flex flex-col gap-2 ${
                        selectedChat?.session_id === c.session_id
                          ? "bg-blue-50 border-blue-200 text-blue-950"
                          : "bg-gray-50/50 border-gray-100 hover:bg-gray-50 text-gray-700"
                      }`}
                    >
                      <div className="flex justify-between items-center w-full font-semibold">
                        <span className="font-mono">ID: {c.session_id}</span>
                        <span className="text-gray-400">
                          {new Date(c.updated_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="text-gray-500 italic max-w-sm truncate leading-relaxed">
                        Last: {c.last_message}
                      </p>
                      <div className="flex items-center justify-between mt-1 text-[10px] text-gray-400">
                        <span>Messages count: {c.messages_count}</span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-bold">Septic Pro AI</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected chat transcript detail (col-span-8) */}
            <div className="lg:col-span-7 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col max-h-[600px]">
              {selectedChat ? (
                <div className="flex flex-col h-full" data-testid={SEPTIC.chatSessionMessages}>
                  {/* Selected Chat Header */}
                  <div className="bg-gray-50 border-b border-gray-200 p-4 flex items-center justify-between">
                    <div>
                      <h4 className="font-heading font-semibold text-sm text-blue-950">Chat Transcript ID</h4>
                      <p className="text-xs text-gray-400 font-mono">{selectedChat.session_id}</p>
                    </div>
                    <span className="text-xs text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full font-bold">
                      Saved in MongoDB
                    </span>
                  </div>

                  {/* Message bubble loop */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
                    {selectedChat.messages?.map((m, index) => (
                      <div
                        key={index}
                        className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}
                      >
                        <div
                          className={`max-w-[80%] px-4 py-3 rounded-xl text-xs ${
                            m.role === "user"
                              ? "bg-blue-900 text-white rounded-tr-none"
                              : "bg-white text-gray-800 rounded-tl-none border border-gray-100 shadow-sm"
                          }`}
                        >
                          <p className="whitespace-pre-line leading-relaxed">{m.content}</p>
                        </div>
                        <span className="text-[9px] text-gray-400 mt-1 px-1">
                          {m.role === "user" ? "Client" : "Claude Assistant"} • {new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-12 text-center text-gray-400">
                  <MessageSquare size={48} className="text-gray-200 mb-2" />
                  <p className="font-semibold text-gray-500">No Chat Selected</p>
                  <p className="text-xs max-w-xs mt-1">Select an active client session from the left-side panel to review the Claude conversation transcript.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 3: Configuration Settings manager */}
        {!isLoadingData && activeTab === "settings" && settings && (
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 max-w-3xl mx-auto w-full">
            <h3 className="text-xl font-heading text-blue-950 font-bold border-b border-gray-100 pb-4 mb-6">
              Parameter Optimization & Served Areas
            </h3>

            <form onSubmit={handleSaveSettings} className="space-y-6 font-body">
              {/* Septic Base Cost */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 block">Base Installation Cost Rate ($)</label>
                <div className="relative">
                  <span className="absolute left-4 top-3 text-gray-400 text-sm">$</span>
                  <input
                    data-testid={SEPTIC.settingBaseCostInput}
                    type="number"
                    required
                    value={settings.base_replacement_cost}
                    onChange={(e) => setSettings({ ...settings, base_replacement_cost: parseFloat(e.target.value) })}
                    className="w-full h-11 pl-8 pr-4 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm focus:bg-white transition"
                  />
                </div>
                <p className="text-xs text-gray-400">This is the dollar base value multiplied dynamically by property specs to form estimate quotes.</p>
              </div>

              {/* Multipliers row */}
              <div className="grid sm:grid-cols-2 gap-6 pt-2">
                {/* Bedrooms config */}
                <div className="space-y-3 bg-gray-50 p-5 rounded-xl border border-gray-200">
                  <span className="text-xs font-bold text-gray-500 uppercase">Bedroom multipliers</span>
                  <div className="space-y-2">
                    {Object.keys(settings.bedroom_multipliers || {}).map((k) => (
                      <div key={k} className="flex items-center justify-between text-xs font-semibold">
                        <span className="text-gray-600">{k} Bed:</span>
                        <input
                          type="number"
                          step="0.05"
                          value={settings.bedroom_multipliers[k]}
                          onChange={(e) => {
                            const updatedBM = { ...settings.bedroom_multipliers };
                            updatedBM[k] = parseFloat(e.target.value);
                            setSettings({ ...settings, bedroom_multipliers: updatedBM });
                          }}
                          className="w-20 h-8 px-2 bg-white border border-gray-300 rounded text-center"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Soil config */}
                <div className="space-y-3 bg-gray-50 p-5 rounded-xl border border-gray-200">
                  <span className="text-xs font-bold text-gray-500 uppercase">Soil difficulty multipliers</span>
                  <div className="space-y-2">
                    {Object.keys(settings.soil_multipliers || {}).map((k) => (
                      <div key={k} className="flex items-center justify-between text-xs font-semibold">
                        <span className="text-gray-600 capitalize">{k}:</span>
                        <input
                          type="number"
                          step="0.05"
                          value={settings.soil_multipliers[k]}
                          onChange={(e) => {
                            const updatedSM = { ...settings.soil_multipliers };
                            updatedSM[k] = parseFloat(e.target.value);
                            setSettings({ ...settings, soil_multipliers: updatedSM });
                          }}
                          className="w-20 h-8 px-2 bg-white border border-gray-300 rounded text-center"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Serviced Locations text area */}
              <div className="space-y-2 pt-2">
                <label className="text-sm font-semibold text-gray-700 block">Active Dispatch Zip Codes</label>
                <textarea
                  rows={3}
                  required
                  value={zipsString}
                  onChange={(e) => setZipsString(e.target.value)}
                  placeholder="90210, 10001, 75001"
                  className="w-full p-4 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm focus:bg-white transition"
                ></textarea>
                <p className="text-xs text-gray-400">Comma-separated values of serviced municipal boundaries. If user’s input matches, they unlock instant dispatch scheduling.</p>
              </div>

              {/* Save Configurations Submit */}
              <div className="pt-4 border-t border-gray-100">
                <button
                  data-testid={SEPTIC.settingSaveButton}
                  type="submit"
                  disabled={isSavingSettings}
                  className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white font-bold tracking-wide rounded-xl shadow transition disabled:bg-gray-100"
                >
                  {isSavingSettings ? <RefreshCw className="animate-spin" size={16} /> : "Save Pricing and Location Parameters"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

// --- APP ROOT COMPONENT ---
function App() {
  const [toasts, setToasts] = useState([]);
  const [lockedSpecs, setLockedSpecs] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const addToast = (message, type = "success") => {
    const id = Date.now() + Math.random().toString(36).substr(2, 5);
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <HelmetProvider>
    <BrowserRouter>
      <div className="flex flex-col min-h-screen bg-slate-50 relative font-body pb-14 md:pb-0">
        {/* Navigation Fixed Header */}
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-gray-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 h-18 flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center group transition" data-testid="site-logo">
              <img src={LOGO_URL} alt="Kool Plumbing" className="h-16 w-auto group-hover:opacity-90 transition" />
            </Link>

            {/* Menu options */}
            <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-gray-600">
              <a href="/#services" className="hover:text-blue-900 transition font-medium">Services</a>
              <a
                data-testid={SEPTIC.navCalculator}
                href="/#calculator"
                className="hover:text-blue-900 transition font-medium"
              >
                Cost Estimator
              </a>
              <Link
                to="/service-areas"
                data-testid="nav-service-areas"
                className="hover:text-blue-900 transition font-medium"
              >
                Service Areas
              </Link>
              <a
                data-testid={SEPTIC.navContact}
                href="/#quote-section"
                className="hover:text-blue-900 transition font-medium"
              >
                Request Quote
              </a>
              <Link
                data-testid={SEPTIC.navAdmin}
                to="/admin"
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-blue-950 font-bold tracking-wide rounded-xl border border-gray-200 transition text-xs flex items-center gap-1.5 shadow-sm"
              >
                <User size={13} /> Admin Panel
              </Link>
            </nav>

            {/* Mobile hamburger toggle */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label="Toggle menu"
              className="md:hidden p-2 bg-slate-100 border border-gray-200 rounded-xl hover:bg-slate-200 transition text-blue-950"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          {/* Mobile dropdown menu */}
          {mobileMenuOpen && (
            <nav data-testid="mobile-menu" className="md:hidden border-t border-gray-200 bg-white/95 backdrop-blur-md px-6 py-4 flex flex-col gap-1 text-sm font-semibold text-gray-700 animate-fade-in">
              <a href="/#services" onClick={() => setMobileMenuOpen(false)} className="py-3 border-b border-gray-100 hover:text-blue-900">Services</a>
              <a href="/#calculator" onClick={() => setMobileMenuOpen(false)} className="py-3 border-b border-gray-100 hover:text-blue-900">Cost Estimator</a>
              <Link to="/service-areas" data-testid="mobile-nav-service-areas" onClick={() => setMobileMenuOpen(false)} className="py-3 border-b border-gray-100 hover:text-blue-900">Service Areas</Link>
              <a href="/#quote-section" onClick={() => setMobileMenuOpen(false)} className="py-3 border-b border-gray-100 hover:text-blue-900">Request Quote</a>
              <a href="tel:+17866224963" className="py-3 border-b border-gray-100 text-amber-700 flex items-center gap-2"><Phone size={15} /> (786) 622-4963</a>
              <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="mt-2 py-3 bg-blue-900 text-white rounded-xl text-center flex items-center justify-center gap-2"><User size={15} /> Admin Panel</Link>
            </nav>
          )}
        </header>

        {/* Global Toast Container */}
        {toasts.map(t => (
          <Toast
            key={t.id}
            message={t.message}
            type={t.type}
            onClose={() => removeToast(t.id)}
          />
        ))}

        {/* Floating Septic Claude AI Chatbot */}
        <ChatbotWidget addToast={addToast} />

        {/* Conversion: sticky mobile call/quote bar */}
        <StickyMobileCTA />

        {/* Routing Structure */}
        <Routes>
          <Route path="/" element={<PublicLanding addToast={addToast} onLockEstimate={setLockedSpecs} />} />
          <Route path="/service-areas" element={<ServiceAreasPage />} />
          <Route path="/service-areas/:slug" element={<LocationServicePage addToast={addToast} />} />
          <Route path="/services/:slug" element={<ServicePage addToast={addToast} />} />
          <Route path="/resources/septic-replacement-guide" element={<GuidePage />} />
          <Route path="/admin" element={<AdminDashboard addToast={addToast} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
